<template>
  <!-- Add New IP Address -->
  <div class="modal fade" id="add_ban" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Add New IP Address</h4>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="handleCreate">
          <div class="modal-body pb-0">
            <div class="row">
              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label">IP Address</label>
                  <input
                    v-model.trim="formAdd.ip_address"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 196.201.214.10"
                  />
                </div>
                <div class="mb-3">
                  <label class="form-label">Reason</label>
                  <textarea
                    v-model.trim="formAdd.reason"
                    class="form-control"
                    rows="3"
                    placeholder="Optional reason for banning this IP"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <a
              href="javascript:void(0);"
              class="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </a>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading || !formAdd.ip_address"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Add IP Address
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add New IP Address -->

  <!-- Edit IP Address -->
  <div class="modal fade" id="edit_ban" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" v-if="current">
        <div class="modal-header">
          <h4 class="modal-title">Edit IP Address</h4>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="handleUpdate">
          <div class="modal-body pb-0">
            <div class="row">
              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label">IP Address</label>
                  <input
                    v-model.trim="formEdit.ip_address"
                    type="text"
                    class="form-control"
                  />
                </div>
                <div class="mb-3">
                  <label class="form-label">Reason</label>
                  <textarea
                    v-model.trim="formEdit.reason"
                    class="form-control"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <a
              href="javascript:void(0);"
              class="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </a>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading || !formEdit.ip_address"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Save Changes
            </button>
          </div>
        </form>
      </div>
      <!-- small safe-guard if no current selected -->
      <div class="modal-content" v-else>
        <div class="modal-body text-center py-5">
          <p class="mb-0">No IP selected for editing.</p>
        </div>
      </div>
    </div>
  </div>
  <!-- /Edit IP Address -->

  <!-- Delete Modal -->
  <div class="modal fade" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" v-if="current">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Deletion</h4>
          <p class="mb-3">
            Are you sure you want to remove the ban for
            <strong>{{ current.ip_address }}</strong
            >?
          </p>
          <div class="d-flex justify-content-center">
            <a
              href="javascript:void(0);"
              class="btn btn-light me-3"
              data-bs-dismiss="modal"
            >
              Cancel
            </a>
            <button
              type="button"
              class="btn btn-danger"
              @click="handleDelete"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
      <div class="modal-content" v-else>
        <div class="modal-body text-center py-5">
          <p class="mb-0">No IP selected for deletion.</p>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>

<script setup>
import { computed, reactive, watch } from "vue";
import { useBannedIpStore } from "@/stores/settings/banned-ip.store";

const bannedIpStore = useBannedIpStore();

const loading = computed(() => bannedIpStore.loading);
const current = computed(() => bannedIpStore.current);

// --- modal helpers: close + clean backdrops ---
function hideModal(id) {
  const el = document.getElementById(id);
  if (!el) return;

  try {
    const bootstrap = window.bootstrap;
    if (bootstrap && bootstrap.Modal) {
      let instance = bootstrap.Modal.getInstance(el);
      if (!instance) {
        instance = new bootstrap.Modal(el);
      }
      instance.hide();
    } else {
      // fallback: manually hide
      el.classList.remove("show");
      el.style.display = "none";
    }
  } catch (e) {
    // ignore
  }

  // clean up any leftover backdrops & body class
  const backdrops = document.querySelectorAll(".modal-backdrop");
  backdrops.forEach((bd) => bd.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("padding-right");
}

// --- Add form ---
const formAdd = reactive({
  ip_address: "",
  reason: "",
});

function resetAddForm() {
  formAdd.ip_address = "";
  formAdd.reason = "";
}

async function handleCreate() {
  if (!formAdd.ip_address) return;
  // simple client-side validation (optional)
  try {
    await bannedIpStore.create({
      ip_address: formAdd.ip_address,
      reason: formAdd.reason || null,
    });
    resetAddForm();
    hideModal("add_ban");
  } catch (e) {
    // errors are already toasted in store
  }
}

// --- Edit form ---
const formEdit = reactive({
  ip_address: "",
  reason: "",
});

// whenever current changes, sync edit form
watch(
  current,
  (val) => {
    if (val) {
      formEdit.ip_address = val.ip_address || "";
      formEdit.reason = val.reason || "";
    }
  },
  { immediate: true }
);

async function handleUpdate() {
  if (!current.value || !formEdit.ip_address) return;
  try {
    await bannedIpStore.update(current.value.id, {
      ip_address: formEdit.ip_address,
      reason: formEdit.reason || null,
    });
    hideModal("edit_ban");
  } catch (e) {
    // store already handles toast
  }
}

// --- Delete handler ---
async function handleDelete() {
  if (!current.value) return;
  try {
    await bannedIpStore.remove(current.value.id);
    hideModal("delete_modal");
  } catch (e) {
    // store already handles toast
  }
}
</script>
